import { useCallback, useEffect, useMemo } from 'react';

import { useWindowFocus } from '@web/common/hooks/useWindowFocus';
import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useMarkConversationAsReadAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useMarkConversationAsReadAction';
import { useMessageConfetti } from '@web/conversations/hooks/useMessageConfetti';
import { getLastMessage } from '@web/conversations/Conversation/ConversationProvider/conversationProvider.utils';

import { getMessageUIFlags } from './conversationMessages.utils';

export const useConversationMessages = () => {
  const { firstUnreadMessageId, markedMessage, messages, newestMessageId, unreadMessagesCount } =
    useConversationState();
  const { tryConfetti } = useMessageConfetti();

  const visibleMessages = useMemo(
    () => [...messages.visibleIds].reverse().map((id) => messages.byId[id]),
    [messages]
  );
  const hasNewestMessageVisible = newestMessageId && messages.visibleIds.includes(newestMessageId);
  const newestMessage = hasNewestMessageVisible ? messages.byId[newestMessageId] : undefined;

  useEffect(() => {
    // @INFO animation will be still possibly triggered by message socket listener,
    // if window is not focused
    if (!document.hasFocus() || !unreadMessagesCount) return;

    visibleMessages.slice(-unreadMessagesCount).forEach(tryConfetti);
  }, [tryConfetti, unreadMessagesCount, visibleMessages]);

  const getUIFlags = useCallback(
    (index: number) => {
      const currentMessage = visibleMessages[index];
      const precedingMessage = visibleMessages[index - 1];

      return getMessageUIFlags({
        firstUnreadMessageId,
        markedMessage,
        message: currentMessage,
        newestMessage,
        precedingMessage,
      });
    },
    [firstUnreadMessageId, markedMessage, newestMessage, visibleMessages]
  );

  return {
    getUIFlags,
    visibleMessages,
  };
};

export const useUnreadMarker = () => {
  const isWindowFocused = useWindowFocus();
  const { action: markConversationAsRead } = useMarkConversationAsReadAction();
  const { hasCustomUnreadMarker, messages, unreadMessagesCount } = useConversationState();

  const shouldMarkAsRead = !hasCustomUnreadMarker && unreadMessagesCount && isWindowFocused;

  useEffect(() => {
    if (!shouldMarkAsRead) return;

    const lastMessage = getLastMessage(messages);
    if (!lastMessage) return;

    markConversationAsRead({ conversationId: lastMessage.conversationId, message: lastMessage });
  }, [markConversationAsRead, messages, shouldMarkAsRead]);
};

export const useScrollToMarkedMessage = () => {
  const {
    markedMessage: { id },
  } = useConversationState();

  const scrollToMessageRef = useCallback(
    (node: HTMLDivElement) => {
      if (!id || !node) return;

      // @INFO call the scroll method after inifnity list adjusted initial scroll position
      setTimeout(() => {
        node.scrollIntoView();
      });
    },
    [id]
  );

  return scrollToMessageRef;
};
